import Form, { FormProps } from "@/shared/form/form";
import {
  FieldsType,
  SaveIntent,
} from "@/modules/feed/features/save-post/model/types";
import Input from "@/shared/form/input";
import { WysiwygWithPersonMention } from "@/modules/common/entities/person/wysiwyg-with-person-mention";
import Button from "@/shared/form/button";
import { SubmitButton, SubmitWrapper } from "@/shared/form/button";
import { useTranslations } from "use-intl";
import { FC } from "react";
import { GeneratePostTitleProps } from "@/modules/ai/features/generate-post-title/generate-post-title";

import { PostImage } from "@/modules/feed/features/save-post/ui/post-image";

export type PostFormViewProps = Omit<FormProps<FieldsType>, "onFinish"> & {
  GeneratePostTitleButton?: FC<GeneratePostTitleProps>;
  onFinish: (values: FieldsType, intent: SaveIntent) => void;
};

export function PostFormView({
  GeneratePostTitleButton,
  form,
  onFinish,
  ...props
}: PostFormViewProps) {
  const t = useTranslations("PostForm");

  const [innerForm] = Form.useForm<FieldsType>();
  const thisForm = form ?? innerForm;

  const body = Form.useWatch("body", thisForm);

  // сохранение больше не публикует пост, поэтому одной кнопки «сохранить» мало:
  // черновик должен быть осознанным выбором, а не состоянием по умолчанию
  const submitWith = async (intent: SaveIntent) => {
    const values = await thisForm.validateFields();

    onFinish(values, intent);
  };

  return (
    <Form
      {...props}
      form={thisForm}
      name="basic"
      layout="vertical"
      onFinish={(values) => onFinish(values, "moderation")}
    >
      <Form.Item<FieldsType> name={"id"} hidden>
        <Input />
      </Form.Item>

      <div className={"flex items-center gap-2"}>
        <div className={"grow"}>
          <Form.Item<FieldsType>
            label={t("Title")}
            name="title"
            rules={[{ required: true }]}
            data-testid="create-post-title-label"
          >
            <Input />
          </Form.Item>
        </div>
        {GeneratePostTitleButton && (
          <Form.Item label={" "}>
            <GeneratePostTitleButton
              size={"middle"}
              disabled={!body}
              postBody={body}
              setTitle={(title) => thisForm.setFieldValue("title", title)}
            />
          </Form.Item>
        )}
      </div>

      <Form.Item<FieldsType>
        label={t("Text")}
        name="body"
        rules={[{ required: true }]}
        data-testid="create-post-body-label"
      >
        <WysiwygWithPersonMention />
      </Form.Item>

      <div className={"flex gap-2"}>
        <Form.Item<FieldsType>
          name="media"
          label={t("Photo")}
          rules={[{ required: true }]}
          initialValue={[]}
          data-testid={"save-post-add-media-button"}
        >
          <PostImage />
        </Form.Item>
      </div>

      <SubmitWrapper className={"gap-2"}>
        <Button
          disabled={props.disabled}
          data-testid="save-post-draft-button"
          onClick={() => submitWith("draft")}
        >
          {t("Save as draft")}
        </Button>
        <SubmitButton data-testid="create-post-submit-button">
          {t("Send to moderation")}
        </SubmitButton>
      </SubmitWrapper>
    </Form>
  );
}
